/**
 * Shared helpers for the scoped-deck sub-modules.
 *
 * `store()` is the synchronous Zustand snapshot used by every mutation
 * path. `resolveYjsDeckKey` translates a real `DeckId` into the
 * deckPath-based key that Yjs sub-maps are keyed by — see the comment
 * inside the function for the lookup rule. `deleteDeckNotes` is the
 * one place where both the local `d.remove()` path and
 * `deck-slice.applyServerDeckDelete` (server broadcast) tear down the
 * per-deck Yjs sub-map.
 */

import { useFlashkaStore } from '../internal/store/flashka-store';
import { getActivePersonalScope } from '../yjs/active-deck-docs';
import { noteDocRegistry } from '../yjs/note-doc-registry';

/** Synchronous Zustand snapshot. */
export function store() {
  return useFlashkaStore.getState();
}

/**
 * Per-note Y.Doc keys use the deck's path-based name historically, not
 * the real DeckId. The scoped-deck factory is called with the real
 * DeckId from `sync.decks.deck(id)`; resolve to whichever the docKey
 * scheme expects so every notes accessor works whether the caller
 * passed an id or an already-resolved path.
 *
 * Lookup rules:
 *   1. If `deckProperties[deckId]?.deckPaths?.[0]` exists → use that path.
 *      (caller passed a real DeckId; we found the authoritative first
 *      path the user stored notes under)
 *   2. Otherwise → fall back to `deckId` itself. (caller already passed a
 *      path, OR the deck has no deckPaths — in which case deckId is the
 *      legitimate key)
 */
export function resolveYjsDeckKey(deckId: string): string {
  return store().deckProperties[deckId]?.deckPaths?.[0] ?? deckId;
}

/**
 * Tear down every per-note Y.Doc + IDB store for this deck.
 *
 * Used by `d.remove()` (local user-triggered delete) and
 * `deck-slice.applyServerDeckDelete` (server broadcast). The server's
 * `deck-delete` handler also runs a `deleteDocsByPrefix` cascade —
 * this client-side step is the IndexedDB equivalent so cached state
 * doesn't outlive the deletion locally.
 *
 * No-op if no personal connection is active (signed out / pre-hydration).
 */
export function deleteDeckNotes(deckId: string): void {
  const scope = getActivePersonalScope();
  if (!scope) return;
  void noteDocRegistry.eraseAndForgetByDeck({
    party: scope.party,
    room: scope.room,
    deckId: resolveYjsDeckKey(deckId),
  });
}
